import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Plus,
  Trash2,
  Loader,
  GitBranch,
  Server,
  Shield,
  Network,
  Route,
  Boxes,
  Container,
  HardDrive,
  Layers,
  ArrowRight,
  ArrowLeft,
  Globe,
  Wifi,
  Monitor,
  CalendarDays,
  AlignLeft,
  type LucideIcon,
} from "lucide-react";
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  MarkerType,
  type ReactFlowInstance,
  type Node,
  type Edge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  getCiDevice,
  listCiDevices,
  getDeviceRelationships,
  addDeviceRelationship,
  deleteDeviceRelationship,
  DeviceResponse,
  DeviceType,
  CiRelationshipResponse,
  DeviceRelationshipGraphResponse,
  RelationshipType,
} from "../../api/frameworksApi";
import { useToastStore } from "../../stores/toastStore";
import { RBACAction } from "../components/RBACButton";
import {
  Modal,
  FormField,
  FormSelect,
  ModalFooterButtons,
} from "../components/Modal";
import { Breadcrumb } from "../components/Breadcrumb";

// ─── Constants ────────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<DeviceType, string> = {
  SERVER: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
  VM: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",
  FIREWALL: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
  SWITCH:
    "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
  ROUTER:
    "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400",
  LB: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  CONTAINER: "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-400",
  STORAGE:
    "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400",
};

const TYPE_ICONS: Record<DeviceType, LucideIcon> = {
  SERVER: Server,
  VM: Boxes,
  FIREWALL: Shield,
  SWITCH: Network,
  ROUTER: Route,
  LB: Route,
  CONTAINER: Container,
  STORAGE: HardDrive,
};

const STATUS_COLORS: Record<string, string> = {
  ACTIVE:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  INACTIVE: "bg-gray-100 text-gray-600 dark:bg-gray-500/20 dark:text-gray-400",
  MAINTENANCE:
    "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
  DECOMMISSIONED:
    "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
  RUNNING:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
};

const STATUS_BADGE_COLORS: Record<string, string> = {
  ACTIVE: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25",
  INACTIVE: "bg-gray-500/15 text-gray-400 border border-gray-500/25",
  MAINTENANCE: "bg-amber-500/15 text-amber-400 border border-amber-500/25",
  DECOMMISSIONED: "bg-red-500/15 text-red-400 border border-red-500/25",
  RUNNING: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25",
};

const REL_TYPE_LABELS: Record<RelationshipType, string> = {
  HOSTED_ON: "Hosted On",
  CONNECTED_TO: "Connected To",
  BEHIND: "Behind",
  ATTACHED_TO: "Attached To",
  PROTECTED_BY: "Protected By",
};

const EDGE_LABELS: Record<RelationshipType, string> = {
  HOSTED_ON: "runs on",
  CONNECTED_TO: "connected to",
  BEHIND: "behind",
  ATTACHED_TO: "attached to",
  PROTECTED_BY: "protected by",
};

type SectionKey = keyof Pick<
  DeviceRelationshipGraphResponse,
  | "hostedOn"
  | "connectedTo"
  | "behind"
  | "attachedTo"
  | "protectedBy"
  | "hosts"
  | "connectedFrom"
  | "backends"
  | "attachedFrom"
  | "protects"
>;

const SECTIONS: { key: SectionKey; label: string; isIncoming: boolean }[] = [
  { key: "hostedOn", label: "HOSTED ON", isIncoming: false },
  { key: "connectedTo", label: "CONNECTED TO", isIncoming: false },
  { key: "behind", label: "BEHIND", isIncoming: false },
  { key: "attachedTo", label: "ATTACHED TO", isIncoming: false },
  { key: "protectedBy", label: "PROTECTED BY", isIncoming: false },
  { key: "hosts", label: "HOSTS", isIncoming: true },
  { key: "connectedFrom", label: "CONNECTED FROM", isIncoming: true },
  { key: "backends", label: "BACKENDS", isIncoming: true },
  { key: "attachedFrom", label: "ATTACHED FROM", isIncoming: true },
  { key: "protects", label: "PROTECTS", isIncoming: true },
];

const ALL_REL_TYPES: RelationshipType[] = [
  "HOSTED_ON",
  "CONNECTED_TO",
  "BEHIND",
  "ATTACHED_TO",
  "PROTECTED_BY",
];

const NODE_W = 200;
const NODE_GAP = 56;
const ROW_H = 260;

const TYPE_HEX: Record<DeviceType, string> = {
  SERVER: "#60a5fa",
  VM: "#c084fc",
  FIREWALL: "#f87171",
  SWITCH: "#fbbf24",
  ROUTER: "#fb923c",
  LB: "#34d399",
  CONTAINER: "#22d3ee",
  STORAGE: "#94a3b8",
};

const REL_EDGE_COLOR: Record<string, string> = {
  "runs on": "#818cf8",
  "connected to": "#34d399",
  behind: "#fb923c",
  "attached to": "#fbbf24",
  "protected by": "#f87171",
};

const SECTION_ICONS: Record<string, LucideIcon> = {
  Connectivity: Wifi,
  "Operating System": Monitor,
  Hardware: Server,
  Virtualisation: Boxes,
  Container: Container,
  Storage: HardDrive,
};

function buildGraph(data: DeviceRelationshipGraphResponse): {
  nodes: Node[];
  edges: Edge[];
} {
  const nodesMap = new Map<string, Node>();
  const edges: Edge[] = [];
  const seen = new Set<string>();

  function addNode(
    id: string,
    name: string,
    type: DeviceType,
    status: string,
    row: number,
    colIndex: number,
    colTotal: number,
    isRoot: boolean,
  ) {
    if (seen.has(id)) return;
    seen.add(id);
    const totalW = colTotal * (NODE_W + NODE_GAP) - NODE_GAP;
    const x = colIndex * (NODE_W + NODE_GAP) - totalW / 2;
    const y = row * ROW_H;
    nodesMap.set(id, {
      id,
      type: "deviceNode",
      position: { x, y },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
      style: { width: NODE_W },
      data: { label: name, type, status, isRoot },
    });
  }

  const outgoing: ({ edgeLabel: string } & CiRelationshipResponse)[] = [
    ...data.hostedOn.map((r) => ({ ...r, edgeLabel: EDGE_LABELS.HOSTED_ON })),
    ...data.connectedTo.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABELS.CONNECTED_TO,
    })),
    ...data.behind.map((r) => ({ ...r, edgeLabel: EDGE_LABELS.BEHIND })),
    ...data.attachedTo.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABELS.ATTACHED_TO,
    })),
    ...data.protectedBy.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABELS.PROTECTED_BY,
    })),
  ];

  const incoming: ({ edgeLabel: string } & CiRelationshipResponse)[] = [
    ...data.hosts.map((r) => ({ ...r, edgeLabel: EDGE_LABELS.HOSTED_ON })),
    ...data.connectedFrom.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABELS.CONNECTED_TO,
    })),
    ...data.backends.map((r) => ({ ...r, edgeLabel: EDGE_LABELS.BEHIND })),
    ...data.attachedFrom.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABELS.ATTACHED_TO,
    })),
    ...data.protects.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABELS.PROTECTED_BY,
    })),
  ];

  const uniqueOut = Array.from(
    new Map(outgoing.map((r) => [r.peerCiId, r])).values(),
  );
  const uniqueIn = Array.from(
    new Map(incoming.map((r) => [r.peerCiId, r])).values(),
  );
  const maxCols = Math.max(uniqueOut.length, uniqueIn.length, 1);

  addNode(
    data.deviceId,
    data.deviceName,
    data.deviceType,
    data.deviceStatus,
    1,
    Math.floor(maxCols / 2),
    maxCols,
    true,
  );
  uniqueIn.forEach((r, i) =>
    addNode(
      r.peerCiId,
      r.peerName,
      r.peerType,
      r.peerStatus,
      0,
      i,
      uniqueIn.length,
      false,
    ),
  );
  uniqueOut.forEach((r, i) =>
    addNode(
      r.peerCiId,
      r.peerName,
      r.peerType,
      r.peerStatus,
      2,
      i,
      uniqueOut.length,
      false,
    ),
  );

  function makeEdge(
    id: string,
    source: string,
    target: string,
    label: string,
  ): Edge {
    const color = REL_EDGE_COLOR[label] ?? "#8b93ff";
    return {
      id,
      source,
      target,
      label,
      type: "bezier",
      animated: true,
      markerEnd: { type: MarkerType.ArrowClosed, color, width: 18, height: 18 },
      style: { stroke: color, strokeWidth: 2 },
      labelStyle: { fontSize: 10, fontWeight: 700, fill: color },
      labelBgStyle: { fill: "#0d1117", fillOpacity: 0.9 },
      labelBgPadding: [6, 3] as [number, number],
      labelBgBorderRadius: 6,
    };
  }

  outgoing.forEach((r) =>
    edges.push(makeEdge(r.id, data.deviceId, r.peerCiId, r.edgeLabel)),
  );
  incoming.forEach((r) =>
    edges.push(makeEdge(r.id, r.peerCiId, data.deviceId, r.edgeLabel)),
  );

  return { nodes: Array.from(nodesMap.values()), edges };
}

// ─── Custom React Flow Node ───────────────────────────────────────────────────

function DeviceNode({
  data,
}: {
  data: { label: string; type: DeviceType; status: string; isRoot: boolean };
}) {
  const DeviceTypeIcon = TYPE_ICONS[data.type];
  const accent = TYPE_HEX[data.type] ?? "#8b93ff";
  return (
    <div
      style={{
        borderColor: data.isRoot ? "#818cf8" : accent,
        borderWidth: data.isRoot ? 2 : 1.5,
        boxShadow: data.isRoot
          ? `0 0 0 3px rgba(129,140,248,0.25), 0 8px 32px rgba(0,0,0,0.6)`
          : `0 4px 20px rgba(0,0,0,0.5)`,
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
      }}
      className="relative px-4 py-3 rounded-xl border backdrop-blur-sm min-w-[170px] max-w-[210px]"
    >
      <div
        className="absolute left-0 top-4 bottom-4 w-[3px] rounded-r-full"
        style={{ backgroundColor: accent }}
      />
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: accent,
          width: 9,
          height: 9,
          border: "2px solid #0f172a",
        }}
      />
      <div className="flex items-center gap-2 mb-2.5 pl-3">
        <div
          className="flex-shrink-0 size-6 rounded-md flex items-center justify-center"
          style={{ backgroundColor: `${accent}22` }}
        >
          <DeviceTypeIcon className="size-3.5" style={{ color: accent }} />
        </div>
        <span className="text-sm font-semibold text-white truncate leading-tight">
          {data.label}
        </span>
      </div>
      <div className="flex gap-1.5 flex-wrap pl-3">
        <span
          className={`text-[9px] px-2 py-0.5 rounded-full font-bold tracking-wide ${TYPE_COLORS[data.type]}`}
        >
          {data.type}
        </span>
        <span
          className={`text-[9px] px-2 py-0.5 rounded-full font-bold tracking-wide ${STATUS_COLORS[data.status] ?? "bg-gray-700 text-gray-300"}`}
        >
          {data.status}
        </span>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: accent,
          width: 9,
          height: 9,
          border: "2px solid #0f172a",
        }}
      />
    </div>
  );
}

const nodeTypes = { deviceNode: DeviceNode };

// ─── Info Tile ─────────────────────────────────────────────────────────────────

function InfoTile({
  icon,
  iconBg,
  label,
  children,
}: {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 bg-muted/30 border border-border rounded-xl px-4 py-3 min-w-0 flex-1">
      <div
        className={`flex-shrink-0 size-9 rounded-lg flex items-center justify-center ${iconBg}`}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs d font-medium mb-0.5">{label}</p>
        <div className="text-sm font-semibold text-foreground leading-tight truncate">
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── Device Info Card ─────────────────────────────────────────────────────────

function DeviceInfoCard({ device }: { device: DeviceResponse }) {
  const DeviceTypeIcon = TYPE_ICONS[device.type];
  const accent = TYPE_HEX[device.type];

  const sections: {
    title: string;
    fields: { label: string; value: string | number | null | undefined }[];
  }[] = [];

  const connectivity = [
    { label: "IP Address", value: device.ipAddress },
    { label: "Management IP", value: device.managementIp },
    { label: "Virtual IP", value: device.virtualIp },
    { label: "Hostname", value: device.hostname },
  ].filter((f) => f.value);
  if (connectivity.length)
    sections.push({ title: "Connectivity", fields: connectivity });

  const os = [
    { label: "OS Name", value: device.osName },
    { label: "OS Version", value: device.osVersion },
  ].filter((f) => f.value);
  if (os.length) sections.push({ title: "Operating System", fields: os });

  const hw = [
    {
      label: "CPU Cores",
      value: device.cpuCores != null ? `${device.cpuCores} cores` : null,
    },
    { label: "RAM", value: device.ramGb != null ? `${device.ramGb} GB` : null },
    {
      label: "Disk",
      value: device.diskGb != null ? `${device.diskGb} GB` : null,
    },
  ].filter((f) => f.value);
  if (hw.length) sections.push({ title: "Hardware", fields: hw });

  const virt = [
    { label: "Hypervisor", value: device.hypervisor },
    { label: "Parent Device ID", value: device.parentCiId },
  ].filter((f) => f.value);
  if (virt.length) sections.push({ title: "Virtualisation", fields: virt });

  const container = [
    { label: "Image", value: device.imageName },
    { label: "Namespace", value: device.namespace },
    { label: "Registry URL", value: device.registryUrl },
  ].filter((f) => f.value);
  if (container.length)
    sections.push({ title: "Container", fields: container });

  const storage = [
    {
      label: "Total Capacity",
      value:
        device.totalCapacityTb != null ? `${device.totalCapacityTb} TB` : null,
    },
    {
      label: "Used Capacity",
      value:
        device.usedCapacityTb != null ? `${device.usedCapacityTb} TB` : null,
    },
  ].filter((f) => f.value);
  if (storage.length) sections.push({ title: "Storage", fields: storage });

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Colored accent top bar */}
      <div
        className="h-0.5"
        style={{
          background: `linear-gradient(90deg, ${accent}90, transparent 70%)`,
        }}
      />

      {/* Header */}
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-border">
        <div
          className="size-6 rounded-md flex items-center justify-center"
          style={{ backgroundColor: `${accent}20` }}
        >
          <DeviceTypeIcon className="size-3.5" style={{ color: accent }} />
        </div>
        <span className="text-sm font-semibold text-foreground">
          Device Information
        </span>
      </div>

      {/* Tiles row */}
      <div className="flex flex-wrap gap-3 p-4">
        {/* Type */}
        <InfoTile
          icon={<DeviceTypeIcon className="size-4" style={{ color: accent }} />}
          iconBg=""
          label="Type"
        >
          <span
            className={`px-2 py-0.5 rounded text-xs font-semibold ${TYPE_COLORS[device.type]}`}
          >
            {device.type}
          </span>
        </InfoTile>

        {/* Status */}
        <InfoTile
          icon={
            <span
              className={`size-2 rounded-full inline-block ${
                device.status === "ACTIVE" || device.status === "RUNNING"
                  ? "bg-emerald-400"
                  : device.status === "MAINTENANCE"
                    ? "bg-amber-400"
                    : device.status === "DECOMMISSIONED"
                      ? "bg-red-400"
                      : "bg-gray-400"
              }`}
            />
          }
          iconBg="bg-muted/60"
          label="Status"
        >
          <span
            className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_COLORS[device.status] ?? ""}`}
          >
            {device.status}
          </span>
        </InfoTile>

        {/* IP Address */}
        {device.ipAddress && (
          <InfoTile
            icon={<Wifi className="size-4 text-indigo-400" />}
            iconBg="bg-indigo-500/15"
            label="IP Address"
          >
            <span className="font-mono">{device.ipAddress}</span>
          </InfoTile>
        )}

        {/* Hostname */}
        {device.hostname && (
          <InfoTile
            icon={<Globe className="size-4 text-cyan-400" />}
            iconBg="bg-cyan-500/15"
            label="Hostname"
          >
            {device.hostname}
          </InfoTile>
        )}

        {/* OS */}
        {(device.osName || device.osVersion) && (
          <InfoTile
            icon={<Monitor className="size-4 text-violet-400" />}
            iconBg="bg-violet-500/15"
            label="Operating System"
          >
            {[device.osName, device.osVersion].filter(Boolean).join(" ")}
          </InfoTile>
        )}

        {/* Created */}
        <InfoTile
          icon={<CalendarDays className="size-4 text-green-400" />}
          iconBg="bg-green-500/15"
          label="Created"
        >
          {new Date(device.createdAt).toLocaleDateString()}
        </InfoTile>

        {/* Updated */}
        <InfoTile
          icon={<CalendarDays className="size-4 text-amber-400" />}
          iconBg="bg-amber-500/15"
          label="Updated"
        >
          {new Date(device.updatedAt).toLocaleDateString()}
        </InfoTile>
      </div>

      {/* Description */}
      {device.description && (
        <div className="mx-4 mb-4 flex items-start gap-3 rounded-xl bg-muted/20 border border-border px-4 py-3">
          <AlignLeft className="size-4 d flex-shrink-0 mt-0.5" />
          <p className="text-sm text-foreground/80 leading-relaxed">
            {device.description}
          </p>
        </div>
      )}

      {/* Detail section cards */}
      {sections.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 px-4 pb-4">
          {sections.map((section) => {
            const SectionIcon = SECTION_ICONS[section.title] ?? HardDrive;
            return (
              <div
                key={section.title}
                className="rounded-xl border border-border bg-muted/10 overflow-hidden"
              >
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-muted/20">
                  <SectionIcon className="size-3.5 d" />
                  <span className="text-xs font-semibold tracking-wide d">
                    {section.title}
                  </span>
                </div>
                <div className="p-3 space-y-0">
                  {section.fields.map((f) => (
                    <div
                      key={f.label}
                      className="flex items-center justify-between gap-4 py-2 border-b border-border/40 last:border-0"
                    >
                      <span className="text-xs d">{f.label}</span>
                      <span className="text-sm font-semibold text-foreground text-right break-all">
                        {f.value ?? "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface RelForm {
  relType: RelationshipType | "";
  targetCiId: string;
  notes: string;
}

const defaultRelForm: RelForm = { relType: "", targetCiId: "", notes: "" };

export function CMDBDeviceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [device, setDevice] = useState<DeviceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [relGraph, setRelGraph] =
    useState<DeviceRelationshipGraphResponse | null>(null);
  const [relLoading, setRelLoading] = useState(false);
  const [allDevices, setAllDevices] = useState<DeviceResponse[]>([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [flowInstance, setFlowInstance] = useState<ReactFlowInstance | null>(
    null,
  );

  const [showAddModal, setShowAddModal] = useState(false);
  const [relForm, setRelForm] = useState<RelForm>(defaultRelForm);
  const [relFormError, setRelFormError] = useState<string | null>(null);
  const [addingRel, setAddingRel] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingRelId, setDeletingRelId] = useState<string | null>(null);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const loadDevice = useCallback(async () => {
    if (!id) return;
    try {
      const res = await getCiDevice(id);
      setDevice(res.data);
    } catch {
      useToastStore.getState().showError("Error", "Failed to load device");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadRelationships = useCallback(async () => {
    if (!id) return;
    setRelLoading(true);
    try {
      const res = await getDeviceRelationships(id);
      setRelGraph(res.data);
      const { nodes: n, edges: e } = buildGraph(res.data);
      setNodes(n);
      setEdges(e);
    } catch {
      useToastStore
        .getState()
        .showError("Error", "Failed to load relationships");
    } finally {
      setRelLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadDevice();
    loadRelationships();
    listCiDevices()
      .then((r) => setAllDevices(r.data))
      .catch(() => {});
  }, [loadDevice, loadRelationships]);

  useEffect(() => {
    if (activeTab !== "topology" || !flowInstance || nodes.length === 0) return;
    const t = window.setTimeout(() => {
      flowInstance.fitView({ padding: 0.2, duration: 350 });
    }, 80);
    return () => window.clearTimeout(t);
  }, [activeTab, flowInstance, nodes, edges]);

  const totalRels = relGraph
    ? relGraph.hostedOn.length +
      relGraph.connectedTo.length +
      relGraph.behind.length +
      relGraph.attachedTo.length +
      relGraph.protectedBy.length +
      relGraph.hosts.length +
      relGraph.connectedFrom.length +
      relGraph.backends.length +
      relGraph.attachedFrom.length +
      relGraph.protects.length
    : 0;

  const hasRelationships = totalRels > 0;
  const filteredTargets = allDevices.filter((d) => d.id !== id);

  async function handleAddRelationship() {
    if (!id || !relForm.relType || !relForm.targetCiId) return;
    if (relForm.targetCiId === id) {
      setRelFormError("A device cannot be related to itself");
      return;
    }
    setAddingRel(true);
    setRelFormError(null);
    try {
      await addDeviceRelationship(id, {
        targetCiId: relForm.targetCiId,
        relationshipType: relForm.relType,
        notes: relForm.notes || undefined,
      });
      useToastStore.getState().showSuccess("Success", "Relationship added");
      setShowAddModal(false);
      setRelForm(defaultRelForm);
      loadRelationships();
    } catch (err: any) {
      const status = err?.status ?? err?.response?.status;
      if (status === 409) setRelFormError("This relationship already exists.");
      else if (status === 422)
        setRelFormError(err?.message || "Validation failed.");
      else
        useToastStore
          .getState()
          .showError("Error", "Failed to add relationship");
    } finally {
      setAddingRel(false);
    }
  }

  async function handleDeleteRelationship(relId: string) {
    setDeletingRelId(relId);
    try {
      await deleteDeviceRelationship(relId);
      useToastStore.getState().showSuccess("Success", "Relationship removed");
      setConfirmDeleteId(null);
      loadRelationships();
    } catch {
      useToastStore
        .getState()
        .showError("Error", "Failed to remove relationship");
    } finally {
      setDeletingRelId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="size-6 animate-spin d" />
      </div>
    );
  }

  if (!device) {
    return <div className="d">Device not found.</div>;
  }

  const DeviceTypeIcon = TYPE_ICONS[device.type];

  return (
    <div className="space-y-6">
      {/* Breadcrumb with status badge + segmented control */}
      <Breadcrumb
        parent="Devices"
        parentTo="/cmdb/config-items"
        current={device.name}
        badge={
          <span
            className={`px-2 py-0.5 rounded-md text-xs font-semibold ${STATUS_BADGE_COLORS[device.status] ?? "bg-gray-500/15 text-gray-400 border border-gray-500/25"}`}
          >
            {device.status}
          </span>
        }
        actions={
          <div className="flex items-center bg-muted/40 border border-border rounded-lg p-0.5 gap-0.5">
            <button
              onClick={() => setActiveTab("overview")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                activeTab !== "topology"
                  ? "bg-indigo-600 text-white shadow-sm shadow-indigo-500/30"
                  : "d hover:text-foreground hover:bg-muted/60"
              }`}
            >
              <Layers className="size-3.5" /> Info
            </button>
            <button
              onClick={() => setActiveTab("topology")}
              disabled={!hasRelationships}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                activeTab === "topology"
                  ? "bg-indigo-600 text-white shadow-sm shadow-indigo-500/30"
                  : "d hover:text-foreground hover:bg-muted/60"
              }`}
            >
              <Network className="size-3.5" /> Topology
            </button>
          </div>
        }
      />

      {/* ── Info View ── */}
      {activeTab !== "topology" && (
        <div className="space-y-5">
          {/* Device info card */}
          <DeviceInfoCard device={device} />

          {/* Relationships card */}
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
              <div className="flex items-center gap-2.5">
                <div className="size-6 rounded-md bg-violet-500/15 flex items-center justify-center">
                  <GitBranch className="size-3.5 text-violet-400" />
                </div>
                <span className="text-sm font-semibold text-foreground">
                  Relationships
                </span>
                {totalRels > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-400 text-xs font-semibold border border-indigo-500/20">
                    {totalRels}
                  </span>
                )}
              </div>
              <button
                onClick={() => {
                  setRelForm(defaultRelForm);
                  setRelFormError(null);
                  setShowAddModal(true);
                }}
                className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
              >
                <Plus className="size-4" /> Add Relation
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {relLoading ? (
                <div className="flex items-center gap-2 d py-10 justify-center">
                  <Loader className="size-4 animate-spin" /> Loading
                  relationships…
                </div>
              ) : !hasRelationships ? (
                <div className="text-center py-12 d border border-dashed border-border rounded-xl">
                  <GitBranch className="size-8 mx-auto mb-3 opacity-40" />
                  <p className="font-medium text-sm">No relationships mapped</p>
                  <p className="text-xs mt-1">
                    Click + Add Relation to connect this device.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {SECTIONS.flatMap(({ key, label, isIncoming }) => {
                    const items: CiRelationshipResponse[] =
                      relGraph?.[key] ?? [];
                    return items.map((rel) => {
                      const PeerIcon = TYPE_ICONS[rel.peerType];
                      const peerAccent = TYPE_HEX[rel.peerType];
                      return (
                        <div
                          key={rel.id}
                          className="flex items-center gap-4 px-4 py-3 rounded-xl border border-border bg-muted/10 hover:bg-muted/30 transition-colors group"
                        >
                          {/* Direction + relationship type */}
                          <div className="flex flex-col items-center gap-1 flex-shrink-0 w-24">
                            <div
                              className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md ${
                                isIncoming
                                  ? "bg-blue-500/15 text-blue-400"
                                  : "bg-emerald-500/15 text-emerald-400"
                              }`}
                            >
                              {isIncoming ? (
                                <ArrowLeft className="size-2.5" />
                              ) : (
                                <ArrowRight className="size-2.5" />
                              )}
                              {isIncoming ? "INCOMING" : "OUTGOING"}
                            </div>
                            <span className="text-[9px] d text-center leading-tight font-medium">
                              {label}
                            </span>
                          </div>

                          {/* Peer device info */}
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div
                              className="flex-shrink-0 size-9 rounded-lg flex items-center justify-center border"
                              style={{
                                backgroundColor: `${peerAccent}15`,
                                borderColor: `${peerAccent}35`,
                              }}
                            >
                              <PeerIcon
                                className="size-4"
                                style={{ color: peerAccent }}
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-foreground truncate">
                                {rel.peerName}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <span
                                  className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${TYPE_COLORS[rel.peerType]}`}
                                >
                                  {rel.peerType}
                                </span>
                                {rel.peerIpAddress && (
                                  <span className="text-xs d font-mono">
                                    {rel.peerIpAddress}
                                  </span>
                                )}
                                <span
                                  className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${STATUS_COLORS[rel.peerStatus] ?? ""}`}
                                >
                                  {rel.peerStatus}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Remove button (outgoing only) */}
                          {!isIncoming && (
                            <div className="flex-shrink-0">
                              {confirmDeleteId === rel.id ? (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() =>
                                      handleDeleteRelationship(rel.id)
                                    }
                                    disabled={deletingRelId === rel.id}
                                    className="text-xs px-2.5 py-1 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                                  >
                                    {deletingRelId === rel.id ? (
                                      <Loader className="size-3 animate-spin" />
                                    ) : (
                                      "Confirm"
                                    )}
                                  </button>
                                  <button
                                    onClick={() => setConfirmDeleteId(null)}
                                    className="text-xs px-2.5 py-1 rounded-lg border border-border hover:bg-muted transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <RBACAction module="cmdb" action="delete">
                                  <button
                                    onClick={() => setConfirmDeleteId(rel.id)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 border border-transparent rounded-lg text-red-500 hover:border-red-500 hover:bg-red-500/10 transition-colors text-xs opacity-0 group-hover:opacity-100"
                                  >
                                    <Trash2 className="size-3" /> Remove
                                  </button>
                                </RBACAction>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Topology View ── */}
      {activeTab === "topology" && (
        <div
          className="rounded-xl border border-border overflow-hidden h-[72vh] min-h-[440px] sm:h-[620px]"
          style={{ background: "#080e1a" }}
        >
          <div
            className="flex items-center justify-between px-4 py-2.5 border-b border-white/10"
            style={{ background: "rgba(15,23,42,0.9)" }}
          >
            <div className="flex items-center gap-4">
              <p className="text-xs text-slate-400">
                <span className="font-semibold text-white">{nodes.length}</span>{" "}
                nodes ·{" "}
                <span className="font-semibold text-white">{edges.length}</span>{" "}
                edges
              </p>
              <div className="hidden sm:flex items-center gap-3">
                {Object.entries(REL_EDGE_COLOR).map(([label, color]) => (
                  <span
                    key={label}
                    className="flex items-center gap-1 text-[10px] text-slate-400"
                  >
                    <span
                      className="inline-block w-4 h-0.5 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    {label}
                  </span>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={() =>
                flowInstance?.fitView({
                  padding: 0.35,
                  includeHiddenNodes: true,
                  duration: 400,
                })
              }
              className="text-xs px-3 py-1.5 rounded-lg border border-white/20 text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
            >
              Fit Graph
            </button>
          </div>
          <ReactFlow
            className="cmdb-topology-flow"
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            onInit={setFlowInstance}
            onNodeClick={(_, node) => {
              if (!node.data.isRoot) navigate(`/cmdb/config-items/${node.id}`);
            }}
            fitView
            fitViewOptions={{ padding: 0.35, includeHiddenNodes: true }}
            minZoom={0.15}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#1e2d4a" gap={30} size={1.5} />
            <Controls
              style={{
                background: "rgba(15,23,42,0.85)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8,
              }}
            />
          </ReactFlow>
          <style>{`
            .cmdb-topology-flow .react-flow__controls { box-shadow: 0 6px 20px rgba(0,0,0,0.35); border: 1px solid rgba(99,102,241,0.4); border-radius: 10px; overflow: hidden; }
            .cmdb-topology-flow .react-flow__controls-button { background: #0f172a; border-bottom: 1px solid rgba(148,163,184,0.25); color: #e2e8f0; width: 30px; height: 30px; }
            .cmdb-topology-flow .react-flow__controls-button:hover { background: #1e293b; }
            .cmdb-topology-flow .react-flow__controls-button svg { fill: #e2e8f0; stroke: #e2e8f0; }
            .cmdb-topology-flow .react-flow__attribution { background: rgba(15,23,42,0.9); color: #cbd5e1; border-radius: 6px 0 0 0; border-left: 1px solid rgba(148,163,184,0.2); border-top: 1px solid rgba(148,163,184,0.2); padding: 2px 6px; }
            .cmdb-topology-flow .react-flow__attribution a { color: #cbd5e1; }
          `}</style>
        </div>
      )}

      {/* Add Relationship Modal */}
      <Modal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Relationship"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowAddModal(false)}
            onSubmit={handleAddRelationship}
            submitLabel={addingRel ? "Saving…" : "Save"}
            submitDisabled={
              addingRel || !relForm.relType || !relForm.targetCiId
            }
          />
        }
      >
        <div className="space-y-4">
          <FormField label="Relationship Type" required>
            <FormSelect
              value={relForm.relType}
              onChange={(v) =>
                setRelForm({
                  ...relForm,
                  relType: v as RelationshipType | "",
                  targetCiId: "",
                })
              }
              options={[
                { value: "", label: "Select type…" },
                ...ALL_REL_TYPES.map((t) => ({
                  value: t,
                  label: REL_TYPE_LABELS[t],
                })),
              ]}
            />
          </FormField>

          <FormField label="Target Device" required>
            <FormSelect
              value={relForm.targetCiId}
              onChange={(v) => setRelForm({ ...relForm, targetCiId: v })}
              options={[
                {
                  value: "",
                  label: filteredTargets.length
                    ? "Select device…"
                    : "No devices available",
                },
                ...filteredTargets.map((d) => ({
                  value: d.id,
                  label: `${d.name} (${d.type}${d.ipAddress ? " · " + d.ipAddress : ""})`,
                })),
              ]}
            />
          </FormField>

          <FormField label="Notes">
            <textarea
              value={relForm.notes}
              onChange={(e) =>
                setRelForm({ ...relForm, notes: e.target.value })
              }
              placeholder="Optional notes…"
              rows={2}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm resize-none"
            />
          </FormField>

          {relFormError && (
            <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 px-3 py-2 rounded-lg">
              {relFormError}
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}
